import { just, Maybe, nothing } from 'maybeasy';
import { Resource } from '../../Resource/Types';
import { TPlainTextKey } from '../../Translations';

export type SsoVendor = 'entra' | 'okta' | 'adfs' | 'ping' | 'other';

export type SsoEnvironment = 'production' | 'staging';

export type SsoConnectionStatus =
  | 'awaiting_metadata'
  | 'verifying'
  | 'attributes_check'
  | 'active'
  | 'needs_attention';

export interface ConnectionSummary {
  id: number;
  displayName: string;
  vendor: SsoVendor;
  environment: SsoEnvironment;
  status: SsoConnectionStatus;
  providerName: string;
  assignedDomainCount: number;
}

export interface LoginDomain {
  id: number;
  domain: string;
  ssoConnectionId: Maybe<number>;
}

export interface HubSpDetails {
  entityId: string;
  acsUrl: string;
}

export interface SsoConfigurations {
  connections: ConnectionSummary[];
  loginDomains: LoginDomain[];
  spDetails: HubSpDetails;
}

export type SsoConfigurationsResource = Resource<SsoConfigurations>;

export interface AttributeMappingEntry {
  field: string;
  claim: string;
}

export interface CapturedAttribute {
  name: string;
  value: string;
}

export interface ConnectionSpDetails {
  entityId: string;
  acsUrl: string;
  relayState: string;
  signingCertificate: Maybe<string>;
}

export interface ConnectionDetails {
  id: number;
  displayName: string;
  vendor: SsoVendor;
  environment: SsoEnvironment;
  status: SsoConnectionStatus;
  providerName: string;
  metadataUrl: Maybe<string>;
  attributeMapping: AttributeMappingEntry[];
  requiredAttributes: string[];
  lastLaunchAttributes: Maybe<CapturedAttribute[]>;
  lastLaunchAt: Maybe<Date>;
  lastError: Maybe<string>;
  verifiedAt: Maybe<Date>;
  certificateExpiresAt: Maybe<Date>;
  createdAt: Date;
  spDetails: ConnectionSpDetails;
}

export type ConnectionResource = Resource<ConnectionDetails>;

export interface TestSignInLaunch {
  actionUrl: string;
}

// The two levels are both meaningful, so this cannot collapse to
// Maybe<string>: Nothing means nothing has failed yet, Just(Nothing) means a
// request failed without server-supplied copy (render the generic message),
// and Just(Just(text)) carries copy from the server to render verbatim.
// Collapsing it would render nothing at all for the middle case.
export type SubmitFailure = Maybe<Maybe<string>>;

export interface VendorContent {
  vendor: SsoVendor;
  name: TPlainTextKey;
  description: TPlainTextKey;
}

export const vendorContents: ReadonlyArray<VendorContent> = [
  { vendor: 'entra', name: 'Microsoft Entra ID', description: 'Formerly Azure AD' },
  { vendor: 'okta', name: 'Okta', description: 'Okta Workforce Identity' },
  { vendor: 'adfs', name: 'ADFS', description: 'Active Directory Federation Services' },
  { vendor: 'ping', name: 'PingFederate', description: 'Ping Identity' },
  { vendor: 'other', name: 'Other SAML 2.0', description: 'Any SAML 2.0-compliant provider' },
];

export const vendorName = (vendor: SsoVendor): TPlainTextKey => {
  switch (vendor) {
    case 'entra':
      return 'Microsoft Entra ID';
    case 'okta':
      return 'Okta';
    case 'adfs':
      return 'ADFS';
    case 'ping':
      return 'PingFederate';
    case 'other':
      return 'Other SAML 2.0';
  }
};

export interface StatusContent {
  label: TPlainTextKey;
  variant: 'neutral' | 'amber' | 'green';
}

export const statusContent = (status: SsoConnectionStatus): StatusContent => {
  switch (status) {
    case 'awaiting_metadata':
      return { label: 'Waiting for your identity provider', variant: 'amber' };
    case 'verifying':
      return { label: 'Verifying metadata', variant: 'neutral' };
    case 'attributes_check':
      return { label: 'Attributes need attention', variant: 'amber' };
    case 'active':
      return { label: 'Active', variant: 'green' };
    case 'needs_attention':
      return { label: 'Needs attention', variant: 'amber' };
  }
};

// The profile fields the attribute diagnostic reports on; field names match
// the Cognito user-pool attributes the backend captures and maps.
export interface DiagnosedField {
  field: string;
  label: TPlainTextKey;
  required: boolean;
}

export const diagnosedFields: ReadonlyArray<DiagnosedField> = [
  { field: 'email', label: 'Email', required: true },
  { field: 'given_name', label: 'First name', required: true },
  { field: 'family_name', label: 'Last name', required: true },
  { field: 'custom:employee_number', label: 'Employee ID', required: false },
];

// Default SAML claim per vendor style, mirroring the backend's
// DEFAULT_ATTRIBUTE_MAPPINGS - used to prefill mapping inputs when the
// stored mapping has no entry for a field.
export const defaultClaimFor = (vendor: SsoVendor, field: string): string => {
  const wsFed = (claim: string) => `http://schemas.xmlsoap.org/ws/2005/05/identity/claims/${claim}`;

  switch (vendor) {
    case 'entra':
    case 'adfs':
      switch (field) {
        case 'email':
          return wsFed('emailaddress');
        case 'given_name':
          return wsFed('givenname');
        case 'family_name':
          return wsFed('surname');
        default:
          return '';
      }
    case 'okta':
    case 'ping':
    case 'other':
      switch (field) {
        case 'email':
          return 'email';
        case 'given_name':
          return 'firstName';
        case 'family_name':
          return 'lastName';
        default:
          return '';
      }
  }
};

// The employee number claim arrives under either custom attribute name,
// matching what the backend captures from the existing provider fleet.
export const capturedValue = (
  field: string,
  captured: ReadonlyArray<CapturedAttribute>,
): Maybe<string> => {
  const names = field === 'custom:employee_number' ? [field, 'custom:employeeIdentifier'] : [field];
  const match = captured.find((attribute) => names.includes(attribute.name));
  return match ? just(match.value) : nothing();
};
